import { create } from 'zustand';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import type { Company } from '../types/company';
import { toast } from 'react-hot-toast';
import { withCrmType, isCurrentCrmType } from '../utils/crmType';

interface CompaniesState {
  companies: Company[];
  isLoading: boolean;
  error: string | null;
  addCompany: (company: Omit<Company, 'id'>) => Promise<void>;
  updateCompany: (id: string, company: Partial<Company>) => Promise<void>;
  deleteCompany: (id: string) => Promise<void>;
  deleteAllCompanies: () => Promise<void>;
  loadCompanies: () => Promise<void>;
  seedDemoCompanies: () => void;
}

export const useCompaniesStore = create<CompaniesState>((set) => ({
  companies: [],
  isLoading: false,
  error: null,

  addCompany: async (company) => {
    try {
      const companyWithTimestamps = withCrmType({
        ...company,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: company.status || 'active',
        contacts: company.contacts || [],
        newsletter_consent: company.newsletter_consent || false,
        createdBy: company.createdBy || auth.currentUser?.displayName || 'Utilisateur inconnu'
      });

      const docRef = await addDoc(collection(db, 'companies'), companyWithTimestamps);
      
      set(state => ({
        companies: [...state.companies, { id: docRef.id, ...companyWithTimestamps }],
        error: null
      }));

      toast.success('Entreprise ajoutée avec succès');
    } catch (error) {
      console.error('Error adding company:', error);
      const message = 'Erreur lors de l\'ajout de l\'entreprise';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  updateCompany: async (id, updates) => {
    try {
      const docRef = doc(db, 'companies', id);
      const updatesWithTimestamp = {
        ...updates,
        updatedAt: new Date()
      };

      await updateDoc(docRef, updatesWithTimestamp);

      set(state => ({
        companies: state.companies.map(c => 
          c.id === id ? { ...c, ...updatesWithTimestamp } : c
        ),
        error: null
      }));

      toast.success('Entreprise mise à jour avec succès');
    } catch (error) {
      console.error('Error updating company:', error);
      const message = 'Erreur lors de la mise à jour de l\'entreprise';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  deleteCompany: async (id) => {
    try {
      await deleteDoc(doc(db, 'companies', id));
      set(state => ({
        companies: state.companies.filter(c => c.id !== id),
        error: null
      }));
      toast.success('Entreprise supprimée avec succès');
    } catch (error) {
      console.error('Error deleting company:', error);
      const message = 'Erreur lors de la suppression de l\'entreprise';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  deleteAllCompanies: async () => {
    try {
      const snapshot = await getDocs(collection(db, 'companies'));
      const toDelete = snapshot.docs.filter(d => isCurrentCrmType(d.data() as any));
      await Promise.all(toDelete.map(d => deleteDoc(doc(db, 'companies', d.id))));
      set({ companies: [], error: null });
      toast.success('Toutes les entreprises ont été supprimées');
    } catch (error) {
      console.error('Error deleting all companies:', error);
      const message = 'Erreur lors de la suppression';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  loadCompanies: async () => {
    set({ isLoading: true, error: null });
    try {
      // Load all companies without filtering by userId to allow sharing between roles
      const querySnapshot = await getDocs(collection(db, 'companies'));
      const companies = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })).filter(c => isCurrentCrmType(c)) as Company[];
      
      set({ companies, isLoading: false, error: null });
    } catch (error) {
      console.error('Error loading companies:', error);
      const message = 'Erreur lors du chargement des entreprises';
      set({ isLoading: false, error: message });
      toast.error(message);
      throw error;
    }
  },

  seedDemoCompanies: () => {
    const now = new Date();
    const demoCompanies: Company[] = [
      {
        id: 'demo-comp-1', name: 'Groupe Lemoine SA', sector: 'Conseil & Services', size: 'ETI (250-500 employés)',
        address: '12 quai Saint-Antoine', city: 'Lyon', phone: '04 78 32 14 05', email: 'contact@groupe-lemoine.fr',
        status: 'active', contacts: [{ name: 'Sophie Marchand', role: 'DRH', email: 's.marchand@groupe-lemoine.fr', phone: '04 78 32 14 05' }],
        newsletter_consent: true, createdAt: now, updatedAt: now, createdBy: 'Démo ANTHEA',
      },
      {
        id: 'demo-comp-2', name: 'Industrie Verot & Fils', sector: 'Industrie manufacturière', size: 'PME (50-250 employés)',
        address: '45 rue de la Métallurgie', city: 'Saint-Étienne', phone: '04 77 92 10 00', email: 'contact@verot-fils.fr',
        status: 'active', contacts: [{ name: 'Pierre Verot', role: 'Directeur Général', email: 'p.verot@verot-fils.fr', phone: '04 77 92 10 00' }],
        newsletter_consent: false, createdAt: now, updatedAt: now, createdBy: 'Démo ANTHEA',
      },
      {
        id: 'demo-comp-3', name: 'TechNova Solutions', sector: 'Informatique & Tech', size: 'PME (50-250 employés)',
        address: '18 boulevard Haussmann', city: 'Paris', phone: '01 45 33 88 12', email: 'hello@technova.io',
        status: 'active', contacts: [{ name: 'Karim Benali', role: 'CTO', email: 'k.benali@technova.io', phone: '01 45 33 88 12' }],
        newsletter_consent: true, createdAt: now, updatedAt: now, createdBy: 'Démo ANTHEA',
      },
      {
        id: 'demo-comp-4', name: 'Boulangerie Martin', sector: 'Agroalimentaire', size: 'TPE (1-9 employés)',
        address: '7 place du Marché', city: 'Villeurbanne', phone: '04 78 03 45 67', email: 'contact@boulangerie-martin.fr',
        status: 'active', contacts: [{ name: 'Élodie Martin', role: 'Gérante', email: 'elodie.martin@bm.fr', phone: '04 78 03 45 67' }],
        newsletter_consent: false, createdAt: now, updatedAt: now, createdBy: 'Démo ANTHEA',
      },
      {
        id: 'demo-comp-5', name: 'Transport Girard Logistique', sector: 'Transport & Logistique', size: 'ETI (250-500 employés)',
        address: '23 zone industrielle Sud', city: 'Roanne', phone: '04 77 44 12 89', email: 'info@girard-logistique.fr',
        status: 'active', contacts: [{ name: 'Marc Girard', role: 'Directeur Logistique', email: 'm.girard@girard-logistique.fr', phone: '04 77 44 12 89' }],
        newsletter_consent: true, createdAt: now, updatedAt: now, createdBy: 'Démo ANTHEA',
      },
      {
        id: 'demo-comp-6', name: 'Cabinet Médical du Parc', sector: 'Santé', size: 'TPE (1-9 employés)',
        address: '5 avenue du Parc', city: 'Lyon', phone: '04 78 25 90 34', email: 'secretariat@cabinet-parc.fr',
        status: 'inactive', contacts: [{ name: 'Dr. Anne Lefèvre', role: 'Médecin', email: 'a.lefevre@cabinet-parc.fr', phone: '04 78 25 90 34' }],
        newsletter_consent: false, createdAt: now, updatedAt: now, createdBy: 'Démo ANTHEA',
      },
    ];

    set(state => ({
      companies: [...demoCompanies, ...state.companies.filter(c => !c.id.startsWith('demo-comp-'))],
      error: null,
    }));
    toast.success('6 entreprises de démonstration ajoutées');
  }
}));